import React, { useState } from "react";
import { LuClipboardCheck, LuUsers, LuCalendarCheck, LuTrendingDown, LuChartBar } from "react-icons/lu";
import KPICard from "./KPICard";
import BookingsByDayChart from "./BookingsByDayChart";
import SchedulingDonut from "./SchedulingDonut";
import CrewTable from "./CrewTable";

const SectionHeader = ({ title }) => (
  <div style={{ fontSize: "14px", fontWeight: "600", color: "#374151", marginBottom: "16px", paddingBottom: "8px", borderBottom: "1px solid #F3F4F6" }}>
    {title}
  </div>
);

const Card = ({ title, children }) => (
  <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "20px" }}>
    <div style={{ fontSize: "14px", fontWeight: "600", color: "#374151", marginBottom: "16px" }}>{title}</div>
    {children}
  </div>
);

const InfoTile = ({ label, value }) => (
  <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "14px 16px" }}>
    <div style={{ fontSize: "11px", color: "#9CA3AF", marginBottom: "4px" }}>{label}</div>
    <div style={{ fontSize: "20px", fontWeight: "700", color: "#111827" }}>{value}</div>
  </div>
);

const OperationsTab = ({ metrics: m, currency = "AED", crewSingular = "Crew", crewPlural = "Crew", bookingPluralLabel = "Bookings" }) => {
  const [showCrewTable, setShowCrewTable] = useState(false);
  const bookingLabel = bookingPluralLabel;
  const memberLabel = crewSingular === "Doctor" ? "doctor"
    : crewSingular === "Coach" ? "coach"
    : crewSingular === "Service Provider" ? "service provider"
    : "crew member";

  return (
    <div style={{ animation: "fadeSlideUp 0.2s ease-out both" }}>
      {/* KPI Summary */}
      <SectionHeader title="Key Performance Indicators" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "16px", marginBottom: "24px" }}>
        <KPICard animDelay={0}   icon={LuClipboardCheck} label="Jobs Completed"    current={m.totalJobsCompleted.current}      previous={m.totalJobsCompleted.previous}      metricKey="total_jobs_completed" />
        <KPICard animDelay={50}  icon={LuUsers}          label={`Active ${crewPlural} %`}     current={m.activeCrewPct.current}           previous={m.activeCrewPct.previous}           metricKey="active_crew_pct"      format="percent" />
        <KPICard animDelay={100} icon={LuChartBar}       label={`Total ${bookingLabel}`}    current={m.totalBookings.current}           previous={m.totalBookings.previous}           metricKey="total_bookings" />
        <KPICard animDelay={150} icon={LuCalendarCheck}  label="Completed vs Sched %" current={m.completedVsScheduledPct.current} previous={m.completedVsScheduledPct.previous} metricKey="completed_vs_scheduled_pct" format="percent" />
        <KPICard animDelay={200} icon={LuTrendingDown}   label="Cancellation Rate" current={m.cancellationRatePct.current}     previous={m.cancellationRatePct.previous}     metricKey="cancellation_rate_pct" format="percent" invertDelta />
        <KPICard animDelay={250} icon={LuTrendingDown}   label="No-Show Rate"      current={m.noShowRatePct.current}           previous={m.noShowRatePct.previous}           metricKey="no_show_rate_pct"      format="percent" invertDelta />
      </div>

      {/* Charts row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "20px", marginBottom: "24px" }}>
        <Card title={`${bookingLabel} by Day of Week`}>
          <BookingsByDayChart data={m.bookingsByDay} />
        </Card>
        <Card title="Scheduling Breakdown">
          <SchedulingDonut
            completedVsScheduledPct={m.completedVsScheduledPct.current}
            cancellationRatePct={m.cancellationRatePct.current}
            noShowRatePct={m.noShowRatePct.current}
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "12px" }}>
            {[
              { label: `Total ${bookingLabel}`,  value: m.totalBookings.current },
              { label: "No-Show Rate",    value: `${m.noShowRatePct.current}%` },
              { label: "Avg Jobs / Day",  value: m.avgJobsPerDay.current },
              { label: `Active ${crewPlural}`,     value: m.activeCrew.current },
            ].map(item => (
              <div key={item.label} style={{ background: "#F9FAFB", borderRadius: "6px", padding: "10px 12px" }}>
                <div style={{ fontSize: "11px", color: "#9CA3AF", marginBottom: "2px" }}>{item.label}</div>
                <div style={{ fontSize: "16px", fontWeight: "700", color: "#111827" }}>{item.value ?? "—"}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Time & Reliability */}
      <SectionHeader title="Time & Reliability" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "12px", marginBottom: "24px" }}>
        <InfoTile label="Peak Day"           value={m.peakDay || "—"} />
        <InfoTile label="Peak Time"          value={m.peakTime || "—"} />
        <InfoTile label="Avg Jobs / Day"     value={m.avgJobsPerDay.current ?? "—"} />
        <InfoTile label={`Jobs / ${crewSingular} (day)`}  value={m.jobsPerCrewDaily.current ?? "—"} />
        <InfoTile label={`Jobs / ${crewSingular} (week)`} value={m.jobsPerCrewWeekly.current ?? "—"} />
        <InfoTile label={`Total ${crewPlural}`}         value={m.totalCrew ?? "—"} />
      </div>

      {/* Crew Breakdown */}
      <SectionHeader title={`${crewPlural} Breakdown`} />
      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
          <span style={{ fontSize: "14px", color: "#6B7280" }}>
            {m.crewBreakdown.length} {memberLabel}{m.crewBreakdown.length !== 1 ? "s" : ""}
          </span>
          <button
            onClick={() => setShowCrewTable(v => !v)}
            style={{
              padding: "6px 14px", borderRadius: "6px", fontSize: "13px", fontWeight: "500", cursor: "pointer",
              border: "1px solid #2563EB", background: showCrewTable ? "#2563EB" : "#fff",
              color: showCrewTable ? "#fff" : "#2563EB", transition: "all 0.15s",
            }}
          >
            {showCrewTable ? "Hide Details" : `View ${crewPlural} Details`}
          </button>
        </div>
        {showCrewTable && <CrewTable crewBreakdown={m.crewBreakdown} currency={currency} crewLabel={crewSingular} />}
      </div>
    </div>
  );
};

export default OperationsTab;
